/**
 * /marketplace/wtb/:id — WTB post detail + paid-reply form.
 *
 * Anonymous browse OK. To reply, foreign breeder must sign in,
 * fill the reply form, and pay the $9 unlock — the US poster
 * then gets the reply by email relay.
 */
import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeftIcon as ArrowLeft, GlobeHemisphereWestIcon as GlobeHemisphereWest, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

export default function WtbDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kennelName, setKennelName] = useState("");
  const [breederEmail, setBreederEmail] = useState("");
  const [breederCountry, setBreederCountry] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [askingPrice, setAskingPrice] = useState("");
  const [message, setMessage] = useState("");
  const [venueAck, setVenueAck] = useState(false);

  // Surface return-from-Stripe feedback.
  const replyStatus = searchParams.get("reply");

  useEffect(() => {
    api.get(`/wtb/${id}`).then((r) => setPost(r.data)).catch(() => setPost(null)).finally(() => setLoading(false));
  }, [id]);

  const submit = async () => {
    if (!user) { navigate(`/login?next=/marketplace/wtb/${id}`); return; }
    if (!venueAck) { toast.error("Please acknowledge the venue-only terms."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/me/wtb/${id}/reply`, {
        kennel_name: kennelName.trim(),
        breeder_email: breederEmail.trim().toLowerCase(),
        breeder_country: breederCountry.trim().toUpperCase().slice(0, 2),
        photo_url: photoUrl.trim(),
        asking_price_usd: askingPrice ? Number(askingPrice) || null : null,
        message: message.trim(),
        venue_acknowledged: true,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.success("Reply submitted.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = user ? AppShell : PublicShell;
  if (loading) {
    return <Shell><div className="max-w-3xl mx-auto p-10 text-sm text-[#6B6B6B]">Loading…</div></Shell>;
  }
  if (!post) {
    return (
      <Shell>
        <div className="max-w-3xl mx-auto p-10 text-center" data-testid="wtb-not-found">
          <p className="font-spectral text-2xl mb-2">Post not found</p>
          <Link to="/marketplace/wtb" className="text-crimson underline">Back to the WTB Board</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <SeoHead
        title={`${post.title} · WTB Board · Armstrong K9 Registry`}
        description={`US breeder wants: ${post.breed}. Reply with photos and pricing for $9 — venue only, no escrow.`}
        url={`${webOrigin()}/marketplace/wtb/${id}`}
        type="article"
      />
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="wtb-detail">
        <Link to="/marketplace/wtb" className="inline-flex items-center gap-1 text-[12px] text-[#6B6B6B] hover:text-crimson mb-6">
          <ArrowLeft size={12} weight="bold"/> Back to board
        </Link>

        {replyStatus === "ok" && (
          <div
            className="rounded-xl border border-[#0F8043]/40 bg-[#EFF7EE] p-4 mb-6 flex items-start gap-3"
            data-testid="wtb-reply-ok-banner"
          >
            <CheckCircle size={20} weight="fill" className="text-[#0F8043] flex-shrink-0" />
            <div className="text-[13px] leading-relaxed">
              <p className="font-semibold text-[#0F8043]">Payment received.</p>
              <p className="text-[#3F3F3F] mt-0.5">
                Your reply has been emailed to the US breeder. Reply directly
                from your inbox to continue the conversation off-platform.
              </p>
            </div>
          </div>
        )}

        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">WTB Request</p>
        <h1 className="font-spectral text-3xl tracking-tight leading-tight">{post.title}</h1>
        <div className="flex flex-wrap items-center gap-3 mt-3 text-[13px]">
          <span className="font-semibold">{post.breed}</span>
          {post.sex && post.sex !== "any" && <span>· {post.sex}</span>}
          {post.age_band && post.age_band !== "any" && <span>· {post.age_band}</span>}
          {post.color && <span>· {post.color}</span>}
          {post.breeding_rights_required && (
            <span className="bg-[#FBF7EC] border border-[#C9A227] rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A6A00]">
              Breeding rights required
            </span>
          )}
          {post.budget_max_usd ? (
            <span className="ml-auto font-tabular text-[14px] text-[#8A6A00] font-bold">
              Budget up to ${Number(post.budget_max_usd).toLocaleString()}
            </span>
          ) : null}
        </div>
        <p className="text-[12px] text-[#6B6B6B] mt-2 inline-flex items-center gap-1">
          <GlobeHemisphereWest size={12} weight="duotone" />
          Posted from <strong className="text-ink">{post.poster_kennel}</strong> · {post.poster_country}
        </p>
        {post.notes && (
          <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl p-4 mt-5 text-[13.5px] leading-relaxed whitespace-pre-wrap" data-testid="wtb-post-notes">
            {post.notes}
          </div>
        )}

        <div className="mt-10 border-t border-[#EAEAEA] pt-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-2">Foreign breeder reply</p>
          <h2 className="font-spectral text-2xl tracking-tight leading-tight">Reply with your kennel & photos · $9 unlock</h2>
          <p className="text-[13px] text-[#6B6B6B] mt-2 max-w-2xl leading-relaxed">
            Pay once to send a single direct email to this US breeder. They
            reply directly to you — Armstrong stays out of the negotiation.
            Do not pay if you cannot deliver the dog described above.
          </p>

          <div className="mt-5 space-y-3 max-w-xl">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Kennel name</Label>
              <Input value={kennelName} onChange={(e) => setKennelName(e.target.value)} className="mt-1.5" data-testid="wtb-reply-kennel-name"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Reply-to email</Label>
                <Input value={breederEmail} onChange={(e) => setBreederEmail(e.target.value)} type="email" className="mt-1.5" data-testid="wtb-reply-email"/>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Country (ISO-2)</Label>
                <Input value={breederCountry} onChange={(e) => setBreederCountry(e.target.value.toUpperCase().slice(0, 2))} placeholder="DE" className="mt-1.5 uppercase" maxLength={2} data-testid="wtb-reply-country"/>
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Photo / portfolio URL</Label>
              <Input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://your-kennel.com/dog-photos" className="mt-1.5" data-testid="wtb-reply-photo"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Asking price (USD)</Label>
              <Input type="number" value={askingPrice} onChange={(e) => setAskingPrice(e.target.value)} placeholder="e.g. 4500" className="mt-1.5" data-testid="wtb-reply-price"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Message</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1.5 min-h-[120px]" maxLength={4000} placeholder="Pedigree highlights, health clearances, titles, age/sex, available courier options..." data-testid="wtb-reply-message"/>
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-[12px] leading-relaxed bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
              <input type="checkbox" checked={venueAck} onChange={(e) => setVenueAck(e.target.checked)} className="mt-0.5 accent-[#C9A227]" data-testid="wtb-reply-venue-ack"/>
              <span>
                <strong>Venue acknowledgement.</strong> I understand Armstrong K9
                Registry is the venue only. The $9 unlocks the email handoff —
                Armstrong does not hold the sale price, coordinate transport,
                or screen the buyer. Any agreement is exclusively between me
                and the US breeder.
              </span>
            </label>
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-[12px] text-[#6B6B6B]">
                One-time fee: <strong className="text-ink">$9 USD</strong>
              </p>
              <Button
                onClick={submit}
                disabled={submitting || !kennelName.trim() || !breederEmail.trim() || !breederCountry.trim() || !message.trim() || !venueAck}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="wtb-reply-submit"
              >
                {submitting ? "Redirecting to Stripe…" : "Pay $9 & send reply"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}
